import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { getPublicEnv } from './publicEnv';

const supabaseUrl = getPublicEnv('VITE_SUPABASE_URL');
const supabaseAnonKey = getPublicEnv('VITE_SUPABASE_ANON_KEY');
const isMock = !supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('dummy');

if (isMock) console.log('⚠️ Frontend in MOCK MODE');
const MOCK_SESSION_KEY = 'pairtab.mock.session';

type MockDB = {
  users: any[];
  spaces: any[];
  space_members: any[];
  categories: any[];
  expenses: any[];
  settlements: any[];
  fixed_configs: any[];
  category_defaults: any[];
  budgets: any[];
};

const createMockSupabase = () => {
  const db: MockDB = { users: [], spaces: [], space_members: [], categories: [], expenses: [], settlements: [], fixed_configs: [], category_defaults: [], budgets: [] };
  const storage = typeof window !== 'undefined' ? window.localStorage : null;
  const savedSession = (() => {
    try {
      return storage ? JSON.parse(storage.getItem(MOCK_SESSION_KEY) || 'null') : null;
    } catch {
      return null;
    }
  })();
  let currentUser: any = savedSession?.user || null;
  let currentToken: string | null = savedSession?.token || null;

  const uid = () => crypto.randomUUID();
  const persistSession = () => {
    if (!storage) return;
    if (!currentUser || !currentToken) {
      storage.removeItem(MOCK_SESSION_KEY);
      return;
    }
    storage.setItem(MOCK_SESSION_KEY, JSON.stringify({ user: currentUser, token: currentToken }));
  };
  const matches = (actual: any, expected: any) => {
    if (expected === null) return actual === null || actual === undefined;
    return actual === expected;
  };

  const attachRelations = (table: keyof MockDB, rows: any[], selectClause = '*') => {
    return rows.map((row) => {
      const hydrated = { ...row };

      if (table === 'categories') {
        if (selectClause.includes('fixed_configs')) {
          hydrated.fixed_configs = db.fixed_configs
            .filter((config) => config.category_id === row.id)
            .map((config) => ({ ...config }));
        }

        if (selectClause.includes('category_defaults')) {
          hydrated.category_defaults = db.category_defaults
            .filter((config) => config.category_id === row.id)
            .map((config) => ({ ...config }));
        }
      }

      if (table === 'expenses' && selectClause.includes('category:categories')) {
        const category = db.categories.find((item) => item.id === row.category_id);
        hydrated.category = category
          ? { name: category.name, emoji: category.emoji, is_fixed: category.is_fixed }
          : null;
      }

      return hydrated;
    });
  };

  const find = (table: keyof MockDB, filter?: Record<string, any>, sort?: { col: string; ascending: boolean }, selectClause?: string) => {
    let results = [...db[table]];
    if (filter) {
      for (const [k, v] of Object.entries(filter)) {
        results = results.filter(r => {
          const val = k.split('.').reduce((o, p) => o?.[p], r);
          return matches(val, v);
        });
      }
    }

    if (sort) {
      results.sort((a, b) => {
        const left = a?.[sort.col];
        const right = b?.[sort.col];
        if (left === right) return 0;
        if (left === undefined || left === null) return sort.ascending ? 1 : -1;
        if (right === undefined || right === null) return sort.ascending ? -1 : 1;
        if (left < right) return sort.ascending ? -1 : 1;
        return sort.ascending ? 1 : -1;
      });
    }

    return attachRelations(table, results, selectClause);
  };

  const buildRow = (table: keyof MockDB, item: any) => {
    const timestamp = new Date().toISOString();
    const base = {
      id: uid(),
      created_at: timestamp,
      updated_at: timestamp,
    };

    if (table === 'spaces') {
      return {
        ...base,
        invite_token: generateToken(),
        is_active: true,
        ...item,
      };
    }

    if (table === 'space_members') {
      return {
        ...base,
        avatar_emoji: '👤',
        role: 'member',
        joined_at: timestamp,
        left_at: null,
        ...item,
      };
    }

    if (table === 'categories') {
      const categoryCount = db.categories.filter((category) => category.space_id === item.space_id).length;
      return {
        ...base,
        is_fixed: false,
        is_custom: false,
        is_pinned: false,
        is_hidden: false,
        sort_order: categoryCount,
        ...item,
      };
    }

    if (table === 'expenses') {
      return {
        ...base,
        action: 'split',
        split_ratio: 50,
        is_fixed_autolog: false,
        is_archived: false,
        excluded_members: [],
        ...item,
      };
    }

    if (table === 'fixed_configs') {
      return {
        ...base,
        autopay: false,
        is_paused: false,
        alternate_turn: false,
        ...item,
      };
    }

    if (table === 'category_defaults') {
      return {
        ...base,
        action: 'split',
        split_ratio: 50,
        ...item,
      };
    }

    return { ...base, ...item };
  };

  class QueryChain {
    private table: keyof MockDB;
    private filters: Record<string, any> = {};
    private selectClause = '*';
    private sort?: { col: string; ascending: boolean };

    constructor(table: keyof MockDB, _data?: any) {
      this.table = table;
    }

    select(cols = '*', _orderFn?: Function) { this.selectClause = cols; return this; }
    eq(col: string, val: any) { this.filters[col] = val; return this; }
    neq(_col: string, _val: any) { return this; }
    is(col: string, val: any) { if (val === null) this.filters[col] = null; return this; }
    gte(_col: string, _val: any) { return this; }
    lte(_col: string, _val: any) { return this; }
    order(col: string, opts?: { ascending?: boolean }) {
      this.sort = { col, ascending: opts?.ascending !== false };
      return this;
    }
    limit(_n: number) { return this; }

    async single() {
      const results = find(this.table, this.filters, this.sort, this.selectClause);
      return { data: results[0] || null, error: null };
    }

    async then(cb: Function) {
      const results = find(this.table, this.filters, this.sort, this.selectClause);
      return cb?.({ data: results, error: null });
    }
  }

  return {
    auth: {
      getSession: async () => currentUser
        ? { data: { session: { user: currentUser, access_token: currentToken } }, error: null }
        : { data: { session: null }, error: null },
      exchangeCodeForSession: async (_code: string) => ({
        data: {
          session: currentUser && currentToken
            ? { user: currentUser, access_token: currentToken }
            : null,
          user: currentUser,
        },
        error: null,
      }),
      verifyOtp: async (_params: any) => {
        if (!currentUser) {
          currentToken = 'mock-' + uid();
          currentUser = {
            id: uid(),
            email: 'mock@example.com',
            user_metadata: { display_name: 'Mock User', avatar_emoji: '👤' }
          };
          persistSession();
        }

        return {
          data: {
            session: { user: currentUser, access_token: currentToken },
            user: currentUser,
          },
          error: null,
        };
      },
      signInWithPassword: async ({ email }: any) => {
        currentToken = 'mock-' + uid();
        currentUser = { id: uid(), email, user_metadata: { display_name: email.split('@')[0] || 'User', avatar_emoji: '👤' } };
        persistSession();
        return { data: { user: currentUser, session: { user: currentUser, access_token: currentToken } }, error: null };
      },
      signUp: async ({ email, options }: any) => {
        currentUser = { id: uid(), email, user_metadata: { display_name: options?.data?.display_name || 'User', avatar_emoji: options?.data?.avatar_emoji || '👤' } };
        currentToken = null;
        persistSession();
        return { data: { user: currentUser, session: null }, error: null };
      },
      resetPasswordForEmail: async (_email: string, _options?: any) => ({
        data: {},
        error: null,
      }),
      resend: async (_params: any) => ({
        data: {},
        error: null,
      }),
      updateUser: async ({ password, ...updates }: any) => {
        if (!currentUser) {
          return { data: { user: null }, error: new Error('No active session') };
        }

        currentUser = {
          ...currentUser,
          ...updates,
          user_metadata: {
            ...currentUser.user_metadata,
            ...updates.data,
          }
        };
        currentToken = currentToken || 'mock-' + uid();
        persistSession();
        return {
          data: {
            user: currentUser,
          },
          error: null,
        };
      },
      signOut: async () => {
        currentUser = null;
        currentToken = null;
        persistSession();
        return { error: null };
      },
      setSession: async ({ access_token, user }: any) => {
        currentToken = access_token;
        currentUser = user || currentUser;
        persistSession();
        return {
          data: {
            session: currentUser && currentToken
              ? { user: currentUser, access_token: currentToken }
              : null,
            user: currentUser,
          },
          error: null,
        };
      }
    },
    from: (table: keyof MockDB) => ({
      select: () => new QueryChain(table),
      insert: (rows: any) => {
        const items = Array.isArray(rows) ? rows : [rows];
        const inserted = items.map((item) => {
          const newItem = buildRow(table, item);
          db[table].push(newItem);
          return { ...newItem };
        });
        return {
          select: () => ({
            single: async () => ({ data: inserted[0] || null, error: null })
          })
        };
      },
      update: (data: any) => ({
        eq: async (col: string, val: any) => {
          const idx = db[table].findIndex(r => r[col] === val);
          if (idx !== -1) {
            db[table][idx] = { ...db[table][idx], ...data, updated_at: new Date().toISOString() };
            return { data: db[table][idx], error: null };
          }
          return { data: null, error: null };
        }
      }),
      delete: () => ({
        eq: async (col: string, val: any) => {
          db[table] = db[table].filter(r => r[col] !== val);
          return { data: null, error: null };
        }
      }),
      upsert: (data: any) => ({
        select: () => ({
          single: async () => {
            const identityKeys = ['id', 'space_id', 'category_id'].filter((key) => key in data);
            const idx = db[table].findIndex((row) => {
              if (identityKeys.length === 0) return false;
              return identityKeys.every((key) => row[key] === data[key]);
            });
            if (idx !== -1) {
              db[table][idx] = { ...db[table][idx], ...data };
              return { data: db[table][idx], error: null };
            } else {
              const newItem = buildRow(table, data);
              db[table].push(newItem);
              return { data: newItem, error: null };
            }
          }
        })
      })
    }),
    channel: () => ({ on: () => ({ subscribe: () => ({ unsubscribe: () => {} }) }) })
  };
};

function generateToken() {
  return Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
}

export const supabase = isMock ? createMockSupabase() as unknown as SupabaseClient : createClient(supabaseUrl, supabaseAnonKey);

export type Database = {
  public: {
    Tables: {
      users: { Row: { id: string; email: string; display_name: string; avatar_emoji: string; is_pro: boolean; pro_trial_ends_at: string | null; created_at: string; }; };
      spaces: { Row: { id: string; name: string; space_type: string; currency: string; start_date: string; invite_token: string; is_active: boolean; }; };
      categories: { Row: { id: string; name: string; emoji: string; is_fixed: boolean; is_custom: boolean; is_pinned: boolean; sort_order: number; }; };
      expenses: { Row: { id: string; amount_cents: number; description: string; action: string; split_ratio: number; created_at: string; }; };
    };
  };
};
